import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useArticlePagination from "../hooks/useArticlePagination";
import ArticleGrid from "../features/articles/ArticleGrid";
import SectionWrapper from "../ui/SectionWrapper";
import articleService from "../../services/articleService";

// News Header Component
const NewsHeader = () => {
  const { t } = useTranslation("home");
  return (
    <div className="text-center mb-12">
      <Link
        to="/news"
        className="text-3xl md:text-4xl font-bold text-white mb-4 hover:text-violet-300 transition-colors duration-300 cursor-pointer block"
      >
        <h2>{t("news.heading")}</h2>
      </Link>
      <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
        {t("news.subtitle")}
      </p>
    </div>
  );
};

// Helper function to format date to dd/mm/yyyy format
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString;
  }
};

// Helper function to transform API article to component format
const transformApiArticle = (apiArticle) => {
  return {
    id: apiArticle.id,
    slug: apiArticle.slug,
    title: apiArticle.title,
    excerpt: apiArticle.summary,
    author: apiArticle.author,
    date: formatDate(apiArticle.publish_date || apiArticle.created_at),
    readTime: apiArticle.read_time || 15,
    category: apiArticle.category,
    articleUrl: `https://www.tadreamk.com/news/${apiArticle.slug}`,
    image: apiArticle.cover_image_url,
    translations: apiArticle.translations,
  };
};

// Main News Component
export default function News() {
  const [apiArticles, setApiArticles] = useState([]);

  // Fetch latest news articles from API (backend only)
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const result = await articleService.getArticles("News", 1, 20);

        if (result.success) {
          setApiArticles(result.data.map(transformApiArticle));
        }
      } catch (error) {
        console.error("Failed to fetch news articles:", error);
      }
    };

    fetchArticles();
  }, []);

  const {
    currentIndex,
    getVisibleArticles,
    handlePrevious,
    handleNext,
    canGoPrevious,
    canGoNext,
    articlesPerPage,
    totalPages,
  } = useArticlePagination(apiArticles);

  return (
    <SectionWrapper>
      <NewsHeader />

      <ArticleGrid
        articles={getVisibleArticles()}
        currentIndex={currentIndex}
        articlesPerPage={articlesPerPage}
        totalPages={totalPages}
        totalArticles={apiArticles.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
      />
    </SectionWrapper>
  );
}
